import { apiClient, ApiResponse } from './api-client'
import {
  Client,
  ClientFilters,
  PaginationOptions,
  ClientListResponse,
  CreateClientDto,
  UpdateClientDto,
  Document,
  ClientStats,
  ClientActivity,
  ClientInteraction
} from '../types/client'

class ClientService {
  private baseUrl = '/api/clients'

  async getAllClients(filters: ClientFilters = {}, pagination: PaginationOptions = { page: 1, limit: 20, sort: 'createdAt:desc' }): Promise<ClientListResponse> {
    const params = new URLSearchParams()
    
    // Add pagination
    params.append('page', pagination.page.toString())
    params.append('limit', pagination.limit.toString())
    if (pagination.sort) params.append('sort', pagination.sort)
    
    // Add filters
    if (filters.search) params.append('search', filters.search)
    if (filters.clientType) params.append('clientType', filters.clientType)
    if (filters.status) params.append('status', filters.status)
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => params.append('tags', tag))
    }
    if (filters.createdBy) params.append('createdBy', filters.createdBy)
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.append('dateTo', filters.dateTo)
    if (filters.hasEmail !== undefined) params.append('hasEmail', filters.hasEmail.toString())
    if (filters.hasPhone !== undefined) params.append('hasPhone', filters.hasPhone.toString())
    if (filters.city) params.append('city', filters.city)
    if (filters.state) params.append('state', filters.state)

    const response = await apiClient.get<ApiResponse<ClientListResponse>>(`${this.baseUrl}?${params}`)
    return response.data.data
  }

  async getClientById(id: string): Promise<Client> {
    const response = await apiClient.get<ApiResponse<{ client: Client }>>(`${this.baseUrl}/${id}`)
    return response.data.data.client
  }

  async createClient(clientData: CreateClientDto): Promise<Client> {
    const response = await apiClient.post<ApiResponse<{ client: Client }>>(this.baseUrl, clientData)
    return response.data.data.client
  }

  async updateClient(id: string, clientData: UpdateClientDto): Promise<Client> {
    const response = await apiClient.put<ApiResponse<{ client: Client }>>(`${this.baseUrl}/${id}`, clientData)
    return response.data.data.client
  }

  async deleteClient(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`)
  }

  async searchClients(query: string, filters: Partial<ClientFilters> = {}): Promise<Client[]> {
    const params = new URLSearchParams()
    params.append('q', query)
    
    if (filters.clientType) params.append('clientType', filters.clientType)
    if (filters.status) params.append('status', filters.status)

    const response = await apiClient.get<ApiResponse<{ clients: Client[] }>>(`${this.baseUrl}/search?${params}`)
    return response.data.data.clients
  }

  async validateDocument(document: string, type: 'CPF' | 'CNPJ'): Promise<{ document: string; type: string; isValid: boolean }> {
    const response = await apiClient.post<ApiResponse<{ document: string; type: string; isValid: boolean }>>(`${this.baseUrl}/validate-document`, {
      document,
      type
    })
    return response.data.data
  }

  // Document management methods
  async uploadDocument(clientId: string, file: File, documentType: string, description?: string): Promise<Document> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType)
    if (description?.trim()) {
      formData.append('descricao', description.trim())
    }

    const response = await apiClient.post<ApiResponse<{ document: Document }>>(`${this.baseUrl}/${clientId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data.data.document
  }

  async getClientDocuments(clientId: string): Promise<Document[]> {
    const response = await apiClient.get<ApiResponse<{ documents: Document[] }>>(`${this.baseUrl}/${clientId}/documents`)
    return response.data.data.documents
  }

  /** Troca a descrição (finalidade) do documento; null apaga. */
  async updateDocumentDescription(clientId: string, documentId: string, description: string | null): Promise<Document> {
    const response = await apiClient.put<ApiResponse<{ document: Document }>>(`${this.baseUrl}/${clientId}/documents/${documentId}`, { descricao: description })
    return response.data.data.document
  }

  async deleteDocument(clientId: string, documentId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${clientId}/documents/${documentId}`)
  }

  async downloadDocument(clientId: string, documentId: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${clientId}/documents/${documentId}/download`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Export methods
  async exportClients(filters: ClientFilters = {}, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const params = new URLSearchParams()
    
    // Add filters
    if (filters.search) params.append('search', filters.search)
    if (filters.clientType) params.append('clientType', filters.clientType)
    if (filters.status) params.append('status', filters.status)
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => params.append('tags', tag))
    }
    if (filters.createdBy) params.append('createdBy', filters.createdBy)
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.append('dateTo', filters.dateTo)
    if (filters.hasEmail !== undefined) params.append('hasEmail', filters.hasEmail.toString())
    if (filters.hasPhone !== undefined) params.append('hasPhone', filters.hasPhone.toString())
    if (filters.city) params.append('city', filters.city)
    if (filters.state) params.append('state', filters.state)

    const response = await apiClient.get(`${this.baseUrl}/export/${format}?${params}`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Audit trail methods
  async getClientAuditTrail(clientId: string): Promise<Array<Record<string, unknown>>> {
    const response = await apiClient.get<ApiResponse<{ auditTrail: Array<Record<string, unknown>> }>>(`${this.baseUrl}/${clientId}/audit`)
    return response.data.data.auditTrail
  }

  // Document preview and management methods
  async getDocumentPreview(clientId: string, documentId: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${clientId}/documents/${documentId}/preview`, {
      responseType: 'blob'
    })
    return response.data
  }

  async getDocumentVersionHistory(_clientId: string, _documentId: string): Promise<Array<Record<string, unknown>>> {
    return []
  }

  // Client dashboard methods
  async getClientStats(userId?: string): Promise<ClientStats> {
    const params = userId ? `?userId=${userId}` : ''
    const response = await apiClient.get<ApiResponse<ClientStats>>(`${this.baseUrl}/stats${params}`)
    return response.data.data
  }

  async getRecentActivity(userId?: string, limit: number = 10): Promise<ClientActivity[]> {
    const params = new URLSearchParams()
    if (userId) params.append('userId', userId)
    params.append('limit', limit.toString())
    
    const response = await apiClient.get<ApiResponse<{ activities: ClientActivity[] }>>(`${this.baseUrl}/activity?${params}`)
    return response.data.data.activities
  }

  async getRecentClients(userId?: string, limit: number = 5): Promise<Client[]> {
    const params = new URLSearchParams()
    if (userId) params.append('userId', userId)
    params.append('limit', limit.toString())
    params.append('sort', 'createdAt:desc')
    
    const response = await apiClient.get<ApiResponse<{ clients: Client[] }>>(`${this.baseUrl}/recent?${params}`)
    return response.data.data.clients
  }

  // Client interactions methods
  async getClientInteractions(clientId: string): Promise<ClientInteraction[]> {
    const response = await apiClient.get<ApiResponse<{ interactions: { data: ClientInteraction[] } }>>(`${this.baseUrl}/${clientId}/interactions?page=1&limit=1000`)
    return response.data.data.interactions.data
  }

  async createInteraction(clientId: string, interaction: Omit<ClientInteraction, 'id' | 'clientId' | 'createdAt' | 'createdBy'>): Promise<ClientInteraction> {
    const response = await apiClient.post<ApiResponse<{ interaction: ClientInteraction }>>(`${this.baseUrl}/${clientId}/interactions`, interaction)
    return response.data.data.interaction
  }

  async updateInteraction(clientId: string, interactionId: string, interaction: Partial<Omit<ClientInteraction, 'id' | 'clientId' | 'createdAt' | 'createdBy'>>): Promise<ClientInteraction> {
    const response = await apiClient.put<ApiResponse<{ interaction: ClientInteraction }>>(`${this.baseUrl}/${clientId}/interactions/${interactionId}`, interaction)
    return response.data.data.interaction
  }

  async deleteInteraction(clientId: string, interactionId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${clientId}/interactions/${interactionId}`)
  }
}

export const clientService = new ClientService()
